import React from 'react';
import { Invitation, SiteRegistrationForm } from '../../types';
import FormFieldSet from '../FormFieldSet';
import { ExclamationTriangleIcon, CheckCircleIcon, MapPinIcon, BuildingOffice2Icon, LinkIcon } from '@heroicons/react/24/outline';
import { MapMarker } from '../../../map/types';
import { ExternalAuthorisation } from '../../../../types';

interface ReviewStepProps {
    formData: SiteRegistrationForm;
    isFormComplete: boolean;
    marker: MapMarker | null;
    invitation?: Invitation | null;
    selectedExternalAuthorisation?: ExternalAuthorisation | null;
    siteMatches?: { [siteId: number]: string };
}

const ReviewStep: React.FC<ReviewStepProps> = ({ formData, isFormComplete, marker, invitation, selectedExternalAuthorisation, siteMatches }) => {
    const hasPrefilled = invitation?.sites && invitation.sites.length > 0;

    return (
        <FormFieldSet legend="Review">
            <div className="space-y-4">
                {/* Status Header */}
                <div className={`alert ${isFormComplete ? 'alert-success' : 'alert-warning'}`}>
                    <div className="flex items-center gap-2">
                        {isFormComplete ? (
                            <>
                                <CheckCircleIcon className="w-5 h-5" />
                                <span className="font-semibold">Ready to submit</span>
                            </>
                        ) : (
                            <>
                                <ExclamationTriangleIcon className="w-5 h-5" />
                                <span className="font-semibold">Form incomplete - please review all steps</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Sites Information */}
                {hasPrefilled ? (
                    <div className="card bg-base-200">
                        <div className="card-body">
                            <h3 className="card-title text-base flex items-center gap-2">
                                <BuildingOffice2Icon className="w-5 h-5" />
                                Sites ({invitation.sites.length})
                            </h3>
                            <div className="space-y-2">
                                {invitation.sites.map((site) => (
                                    <div key={site.id} className="p-3 bg-base-100 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="font-semibold">{site.name}</div>
                                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                                    <MapPinIcon className="w-4 h-4" />
                                                    {site.address}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {site.latitude && site.longitude 
                                                        ? `${Number(site.latitude).toFixed(4)}, ${Number(site.longitude).toFixed(4)}`
                                                        : ''}
                                                </div>
                                            </div>
                                            {siteMatches && siteMatches[site.id] && (
                                                <div className="badge badge-success gap-1">
                                                    <CheckCircleIcon className="w-3 h-3" />
                                                    Connected
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="card bg-base-200">
                        <div className="card-body">
                            <h3 className="card-title text-base flex items-center gap-2">
                                <BuildingOffice2Icon className="w-5 h-5" />
                                Site Information
                            </h3>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-gray-600">Site Name</div>
                                    <div className="font-medium">{formData.name || '-'}</div>
                                </div>
                                <div>
                                    <div className="text-gray-600">Manufacturer</div>
                                    <div className="font-medium">{formData.manufacturer || '-'}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-gray-600">Address</div>
                                    <div className="font-medium">{formData.address || '-'}</div>
                                </div>
                                <div>
                                    <div className="text-gray-600">Latitude</div>
                                    <div className="font-medium">{formData.latitude?.toFixed(6) || '-'}</div>
                                </div>
                                <div>
                                    <div className="text-gray-600">Longitude</div>
                                    <div className="font-medium">{formData.longitude?.toFixed(6) || '-'}</div>
                                </div>
                                {formData.generation_kw && (
                                    <div>
                                        <div className="text-gray-600">Solar Capacity</div>
                                        <div className="font-medium">{formData.generation_kw} kW</div>
                                    </div>
                                )}
                                {formData.storage_capacity_kwh && (
                                    <div>
                                        <div className="text-gray-600">BESS Capacity</div>
                                        <div className="font-medium">{formData.storage_capacity_kwh} kWh</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Manufacturer */}
                {hasPrefilled && (
                    <div className="card bg-base-200">
                        <div className="card-body">
                            <h3 className="card-title text-base">Manufacturer</h3>
                            <div className="text-lg font-semibold">{formData.manufacturer || '-'}</div>
                        </div>
                    </div>
                )}

                {/* Authorization Information */}
                {selectedExternalAuthorisation && (
                    <div className="card bg-base-200">
                        <div className="card-body">
                            <h3 className="card-title text-base flex items-center gap-2">
                                <LinkIcon className="w-5 h-5" />
                                Authorization
                            </h3>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-gray-600">External Manufacturer</div>
                                    <div className="font-medium">{selectedExternalAuthorisation.manufacturer}</div>
                                </div>
                                <div>
                                    <div className="text-gray-600">External Account</div>
                                    <div className="font-medium">{selectedExternalAuthorisation.external_account}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-gray-600">External ID</div>
                                    <div className="font-medium font-mono text-xs">{selectedExternalAuthorisation.external_id}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Matched Sites */}
                {hasPrefilled && siteMatches && Object.keys(siteMatches).length > 0 && (
                    <div className="card bg-base-200">
                        <div className="card-body">
                            <h3 className="card-title text-base">Matched Sites</h3>
                            <div className="space-y-2">
                                {invitation.sites
                                    .filter(site => siteMatches[site.id]?.trim())
                                    .map((site) => (
                                        <div key={site.id} className="flex justify-between items-start text-sm p-2 bg-base-100 rounded">
                                            <div className="flex-1">
                                                <div className="font-medium">{site.name}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{site.address}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs text-gray-600">{siteMatches[site.id]}</span>
                                                <CheckCircleIcon className="w-4 h-4 text-success" />
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            {invitation.sites.filter(site => !siteMatches[site.id]?.trim()).length > 0 && (
                                <div className="alert alert-warning mt-2">
                                    <ExclamationTriangleIcon className="w-4 h-4" />
                                    <span className="text-xs">
                                        {invitation.sites.filter(site => !siteMatches[site.id]?.trim()).length} site(s) not matched. 
                                        These will be skipped during registration.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!hasPrefilled && formData.external_site_id && (
                    <div className="card bg-base-200">
                        <div className="card-body">
                            <h3 className="card-title text-base">External Site ID</h3>
                            <div className="font-mono text-sm">{formData.external_site_id}</div>
                        </div>
                    </div>
                )}
            </div>
        </FormFieldSet>
    );
};

export default ReviewStep;